import React from "react";
import { Box, FormControl, MenuItem, Select, SxProps, Theme } from "@mui/material";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { MESSAGE } from "constants/validate";
import LabelHeader from "components/Label/LabelHeader";
import { SEQUENCE_OPTIONS, STATUS_OPTIONS } from "../utils/categoryFormFields";

interface CategoryDisplayOrderFieldProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  seqName: Path<T>;
  statusName: Path<T>;
  label: string;
  disabled?: boolean;
  sxLabel?: SxProps<Theme>;
  sxBoxField?: SxProps<Theme>;
  sxField?: SxProps<Theme>;
  disabledField?: SxProps<Theme>;
}

/**
 * Reusable dropdown field for react-hook-form.
 */
function DropdownField<T extends FieldValues = FieldValues>({
  name,
  control,
  options,
  disabled,
  fieldStyle,
}: {
  name: Path<T>;
  control: Control<T>;
  options: { label: string; value: any }[];
  disabled?: boolean;
  fieldStyle?: SxProps<Theme>;
}) {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: MESSAGE.EMPTY }}
      render={({ field, fieldState }) => (
        <FormControl size="medium" error={!!fieldState.error} sx={{ width: "50%" }}>
          <Select
            value={field.value ?? 1}
            name={field.name}
            disabled={disabled}
            size="medium"
            error={!!fieldState.error}
            onChange={(event) => field.onChange(Number(event.target.value))}
            onBlur={field.onBlur}
            sx={{
              width: "100%",
              height: "48px",
              "& .MuiSelect-select": {
                padding: "12.5px 14px",
              },
              ...fieldStyle,
              borderColor: fieldState.error ? "var(--red-800)" : "var(--silver-150)",
              backgroundColor: disabled ? "var(--offwhite-400)" : "var(--white)",
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );
}

/**
 * Component to display the Display Order field with two dropdowns: seq (order) and statusFlag (status).
 */
function CategoryDisplayOrderField<T extends FieldValues = FieldValues>({
  control,
  seqName,
  statusName,
  label,
  disabled = false,
  sxLabel,
  sxBoxField,
  sxField,
  disabledField,
}: CategoryDisplayOrderFieldProps<T>) {
  const fieldStyle = disabled && disabledField ? disabledField : sxField;

  return (
    <Box display="flex" alignItems="center" width="100%" marginBottom="20px">
      <LabelHeader label={label} sxBox={{ ...sxLabel }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "2px",
          ...sxBoxField,
        }}
      >
        <DropdownField
          name={seqName}
          control={control}
          options={SEQUENCE_OPTIONS}
          disabled={disabled}
          fieldStyle={fieldStyle}
        />
        <DropdownField
          name={statusName}
          control={control}
          options={STATUS_OPTIONS}
          disabled={disabled}
          fieldStyle={fieldStyle}
        />
      </Box>
    </Box>
  );
}

export default CategoryDisplayOrderField;
